import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getHourTypes } from "../../../_api/HourType";
import {
    COMPANY_SETTING_CODE_FALLBACK_WORK_HOUR_TYPE_ID,
    getCompanySetting,
    upsertCompanySetting,
} from "../../../_api/Company";
import { DefaultDropdown } from "../../../_helpers/components/Forms/Dropdown";
import { Loader } from "../../../_helpers/components/Forms/Loader";

const CompanyParametrageTab = () => {
    const [hourTypeOptions, setHourTypeOptions] = useState([]);
    const [fallbackHourTypeId, setFallbackHourTypeId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const typesResp = await getHourTypes();
            const rawList = typesResp.data;
            const list = Array.isArray(rawList)
                ? rawList
                : Array.isArray(rawList?.data)
                  ? rawList.data
                  : [];
            const opts = list.map((ht) => ({
                value: String(ht.id),
                label: ht.name,
            }));
            setHourTypeOptions(opts);
        } catch {
            toast.error("Impossible de charger les types d’heures.");
            setHourTypeOptions([]);
        }

        try {
            const settingResp = await getCompanySetting(
                COMPANY_SETTING_CODE_FALLBACK_WORK_HOUR_TYPE_ID,
            );
            const raw = settingResp.data?.value;
            const id =
                typeof raw === "number"
                    ? raw
                    : typeof raw === "string" &&
                        raw !== "" &&
                        !Number.isNaN(Number(raw))
                      ? Number(raw)
                      : null;
            setFallbackHourTypeId(id);
        } catch {
            setFallbackHourTypeId(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const persistFallbackHourType = async (nextId) => {
        setSaving(true);
        try {
            const res = await upsertCompanySetting({
                code: COMPANY_SETTING_CODE_FALLBACK_WORK_HOUR_TYPE_ID,
                value: nextId,
            });
            const raw = res.data?.value;
            setFallbackHourTypeId(
                raw === null || raw === undefined || raw === ""
                    ? null
                    : Number(raw),
            );
            toast.success("Enregistré.");
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                "Erreur lors de l'enregistrement.";
            toast.error(msg);
            await load();
        } finally {
            setSaving(false);
        }
    };

    const dropdownOptions = [
        { label: "— Non défini —", value: "" },
        ...hourTypeOptions,
    ];

    const selectValue =
        fallbackHourTypeId != null &&
        hourTypeOptions.some((o) => o.value === String(fallbackHourTypeId))
            ? String(fallbackHourTypeId)
            : "";

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <h1 className="text-3xl font-bold text-gray-900">Paramétrage</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Options avancées de la société
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <Loader text="Chargement des paramètres..." />
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-xl">
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">
                            Planning
                        </h2>
                        <p className="text-sm text-gray-600 mb-4">
                            Type d’heures utilisé par défaut lorsque le réalisé ne permet pas
                            d’en déduire un type (ex. pointage sans type explicite).
                        </p>
                        <DefaultDropdown
                            name="Type d’heures de repli (planning)"
                            options={dropdownOptions}
                            value={selectValue}
                            readOnly={saving}
                            onChange={(e) => {
                                const v = e.target.value;
                                const next = v === "" ? null : Number(v);
                                persistFallbackHourType(next);
                            }}
                        />
                        {fallbackHourTypeId != null &&
                            !hourTypeOptions.some(
                                (o) => o.value === String(fallbackHourTypeId),
                            ) && (
                                <p className="mt-2 text-xs text-amber-700">
                                    La valeur enregistrée ne correspond plus à un type
                                    d’heures connu. Choisissez un type ou enregistrez « Non
                                    défini » pour effacer.
                                </p>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyParametrageTab;
